import { toInteger } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import PptxGenJS from '@meitu/js-to-pptx';
import { newUtil } from '@/utils/dom-to-image';
import { createCanvas } from '@/utils/file-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { pxToInch } from '@/utils/unit-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import { ILayerBehavior, ITableLayer } from '@/version-types';
import { getUsingStyles } from '../utils/get-table-layer-by-xlsx';
import { table2Canvas } from '../utils/table2canvas';

type Behaviors = Pick<
    ILayerBehavior,
    'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync' | 'getLayerImageAsync'
>;

const canvas2File = async (canvas: HTMLCanvasElement): Promise<File> => {
    const blob = await newUtil().canvasToBlob(canvas);
    const file = new File([blob!], uuidv4(), {
        type: 'image/png',
    });
    return file;
};

/** key为layer的json字符串, value为screenshotUrl */
const screenshotUrlCache = new Map<string, string>();

/**
 * screenshotUrl是给移动端展示用的, 移动端展示表格图层只展示图片
 * 表格dom太多时, dom-to-image生图会特别慢(极限值可能需要十几秒)
 * 生成图片时机:
 *  1. 手动保存作图记录时
 *  2. 表格首次创建时
 */
export const getTableLayerScreenshotUrl = async (layer: ITableLayer, frameworkIdentity: string) => {
    const layerJson = JSON.stringify(layer);
    if (screenshotUrlCache.has(layerJson)) {
        return screenshotUrlCache.get(layerJson);
    }

    const canvas = await table2Canvas(layer, frameworkIdentity);
    const file = await canvas2File(canvas);
    const { url: screenshotUrl } = await uploadFileToObs(file);
    console.log('===table screenshotUrl', screenshotUrl);
    screenshotUrlCache.set(layerJson, screenshotUrl);
    return screenshotUrl;
};

export const createSaveBehaviors = (frameworkIdentity: string) => {
    const behaviors: Behaviors = {
        async getLayerImageAsync(layer) {
            const canvas = await table2Canvas(layer as ITableLayer, frameworkIdentity);
            if (!canvas) {
                throw new Error('生成图片失败, 需要排查一下');
            }
            return canvas;
        },

        async onBeforeSaveAsync(_layer, isAutoSave, isDuplicate) {
            const layer = _layer as ITableLayer;
            deleteExtraInfo(layer);
            layer.data.styles = getUsingStyles(layer);

            if (!isAutoSave && !isDuplicate) {
                const screenshotUrl = await getTableLayerScreenshotUrl(layer, frameworkIdentity);
                layer.data.screenshotUrl = screenshotUrl;
            }
        },

        async getResultCanvasAsync(layer, resultSize) {
            // console.time('===========生成时间');
            const { width, height, left, top, rotate } = layer;
            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const centerPoint = {
                x: resultWidth / 2 + left,
                y: resultHeight / 2 + top,
            };
            const drawOffsetPoint = {
                x: -resultWidth / 2,
                y: -resultHeight / 2,
            };
            const layerImage = await behaviors.getLayerImageAsync?.(layer, false);
            if (!layerImage) return;
            const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
            destCtx.translate(centerPoint.x, centerPoint.y);
            destCtx.rotate((Math.PI / 180) * rotate);
            destCtx.drawImage(layerImage, drawOffsetPoint.x, drawOffsetPoint.y);
            // console.timeEnd('===========生成时间');
            return destCanvas;
        },

        async savePPTLayerAsync(slide, layer) {
            const { left, top, width, height, rotate } = layer;
            const options: PptxGenJS.ImageProps = {
                x: pxToInch(left),
                y: pxToInch(top),
                w: pxToInch(width),
                h: pxToInch(height),
                rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
            };

            const canvas = await behaviors.getLayerImageAsync?.(layer);

            options.data = canvas?.toDataURL() || '';

            slide.addImage(options);
        },
    };

    return behaviors;
};
